import {
  motion,
  useScroll,
  useTransform,
  useReducedMotion,
} from "framer-motion";
import { useRef, useState, useEffect } from "react";

const BGIMAGE =
  "https://hlineweb-f6dwh8btedejg7fz.z03.azurefd.net//hlinefiles/HLineWeb/Projects/assets/projects/windows/bg_Image.jpg";

export default function Hero() {
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end start"],
  });
  const prefersReducedMotion = useReducedMotion();

  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const paneW =
    window.innerWidth < 640 ? 180 : window.innerWidth < 1024 ? 300 : 530;

  const move = prefersReducedMotion ? 0 : paneW;
  const leftX = useTransform(scrollYProgress, [0, 0.6, 1], [0, -move, -move]);
  const rightX = useTransform(scrollYProgress, [0, 0.6, 1], [0, move, move]);
  const textY = useTransform(scrollYProgress, [0.1, 0.3], [-80, 0]);
  const textOpacity = useTransform(scrollYProgress, [0.12, 0.3], [0, 1]);
  const textScale = useTransform(scrollYProgress, [0.12, 0.3], [0.98, 1]);

  return (
    <section
      ref={sectionRef}
      className={`relative ${
        isMobile ? "h-[74.7vh]" : "h-[120vh] sm:h-[180vh] md:h-[200vh]"
      }`}
    >
      <div
        className={`${
          isMobile
            ? "relative h-[70vh]"
            : "sticky top-0 h-[65vh] sm:h-screen overflow-hidden"
        }`}
      >
        <div className="absolute inset-0 -z-10">
          <img
            src={BGIMAGE}
            alt="background"
            className={`w-full h-full object-cover ${
              isMobile ? "object-[center_top]" : ""
            } opacity-70`}
          />
          <div className="absolute inset-0 bg-black/25" />
        </div>

        {!isMobile && (
          <motion.div
            initial={{ opacity: 0, scale: 0.995 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="absolute inset-0 flex items-center justify-center z-0"
          >
            <svg
              viewBox="0 0 1200 600"
              className="w-full h-full max-w-none"
              preserveAspectRatio="xMidYMid meet"
            >
              <motion.g style={{ x: leftX }}>
                <GlassPane
                  x={48}
                  y={48}
                  w={paneW}
                  h={window.innerWidth < 640 ? 280 : 504}
                  side="left"
                />
              </motion.g>
              <motion.g style={{ x: rightX }}>
                <GlassPane
                  x={622}
                  y={48}
                  w={paneW}
                  h={window.innerWidth < 640 ? 280 : 504}
                  side="right"
                />
              </motion.g>
            </svg>
          </motion.div>
        )}

        <motion.div
          style={{ y: textY, opacity: textOpacity, scale: textScale }}
          className="absolute inset-0 z-10 flex items-center justify-center pointer-events-none"
        >
          <div
            className={`container px-4 text-center ${isMobile ? "pt-32" : ""}`}
          >
            <p className="text-[10px] sm:text-xs tracking-[0.25em] uppercase text-white/90">
              Aluminium Windows • Doors • Facades
            </p>
            <h1 className="mt-2 text-xl sm:text-4xl md:text-6xl font-extrabold leading-tight text-white">
              Light. Quiet. Built to Last.
            </h1>
            <p className="mt-2 sm:mt-4 text-sm sm:text-base md:text-lg text-white/90">
              Slim profiles, high-performance glazing and custom engineering for
              modern architecture.
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

// GlassPane + LeverHandle (same as before)
function GlassPane({ x, y, w, h, side }) {
  const sash = 12,
    frame = 8,
    gridStroke = 1.6;
  const innerX = x + sash,
    innerY = y + sash,
    innerW = w - sash * 2,
    innerH = h - sash * 2;

  const handleScale = w < 250 ? 0.38 : 0.52;
  const hx = side === "left" ? x + w - (sash + 14) : x + (sash + 14);
  const hy = y + h / 2;
  const gradientId = `glass-${x}-${side}`;

  const cols = 3,
    rows = 3;
  const vXs = [1, 2].map((i) => innerX + (innerW * i) / cols);
  const hYs = [1, 2].map((i) => innerY + (innerH * i) / rows);

  return (
    <g>
      <rect
        x={x}
        y={y}
        width={w}
        height={h}
        fill="rgba(255,255,255,0.06)"
        stroke="rgba(255,255,255,0.32)"
        strokeWidth={frame}
      />

      <defs>
        <linearGradient
          id={gradientId}
          x1={innerX}
          y1={innerY}
          x2={innerX + innerW}
          y2={innerY}
        >
          {side === "left" ? (
            <>
              <stop offset="0%" stopColor="rgba(255,255,255,0.18)" />
              <stop offset="100%" stopColor="rgba(255,255,255,0.05)" />
            </>
          ) : (
            <>
              <stop offset="0%" stopColor="rgba(255,255,255,0.05)" />
              <stop offset="100%" stopColor="rgba(255,255,255,0.18)" />
            </>
          )}
        </linearGradient>
      </defs>

      <rect
        x={innerX}
        y={innerY}
        width={innerW}
        height={innerH}
        fill={`url(#${gradientId})`}
        stroke="rgba(255,255,255,0.14)"
        strokeWidth="1.5"
      />

      {vXs.map((vx, i) => (
        <rect
          key={`v${i}`}
          x={vx - gridStroke / 2}
          y={innerY + 1}
          width={gridStroke}
          height={innerH - 2}
          fill="rgba(255, 255, 255, 0.2)"
        />
      ))}
      {hYs.map((hyLine, i) => (
        <rect
          key={`h${i}`}
          x={innerX + 1}
          y={hyLine - gridStroke / 2}
          width={innerW - 2}
          height={gridStroke}
          fill="rgba(255, 255, 255, 0.2)"
        />
      ))}

      <LeverHandle x={hx} y={hy} side={side} scale={handleScale} />
    </g>
  );
}

function LeverHandle({ x, y, side = "right", scale = 1 }) {
  const id = `lever-${Math.round(x)}-${Math.round(y)}-${side}`;
  const flip = side === "left" ? -1 : 1;

  return (
    <g
      transform={`translate(${x},${y}) scale(${flip * scale},${scale})`}
      pointerEvents="none"
    >
      <defs>
        <linearGradient id={`${id}-metal`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#555" />
          <stop offset="50%" stopColor="#ccc" />
          <stop offset="100%" stopColor="#f4f4f4" />
        </linearGradient>
      </defs>

      <g>
        <rect
          x="-10"
          y="-24"
          width="20"
          height="48"
          rx="8"
          fill={`url(#${id}-metal)`}
          stroke="rgba(0,0,0,0.3)"
          strokeWidth="0.5"
        />
        <rect
          x="5"
          y="-6"
          width="8"
          height="12"
          rx="3"
          fill={`url(#${id}-metal)`}
        />
      </g>

      <g transform={`translate(12,-6)`}>
        <rect
          x="0"
          y="0"
          width="14"
          height="72"
          rx="7"
          fill={`url(#${id}-metal)`}
        />
        <rect
          x="2"
          y="2"
          width="10"
          height="68"
          rx="5"
          fill="rgba(0,0,0,0.18)"
        />
        <rect
          x="2"
          y="3"
          width="10"
          height="2"
          rx="1"
          fill="white"
          opacity="0.6"
        />
        <ellipse cx="7" cy="10" rx="6" ry="4.5" fill={`url(#${id}-shine)`} />
      </g>
    </g>
  );
}
